"use client";

import { usePathname } from "next/navigation";
import { useEffect } from "react";

import { completeOnboardingStep, reportAbandonment, resetOnboardingNavigation, type OnboardingStep } from "./onboarding-funnel";
import { resolvePage } from "./page-view";

/** The attribute a link carries to say which step pressing it completes. */
export const ONBOARDING_STEP_ATTRIBUTE = "data-onboarding-step";

/**
 * Wires the onboarding funnel to the page, on the funnel's routes only (`page_type: onboarding`):
 *
 * - `pagehide` reports the abandonment (`onboarding_abandoned`), once;
 * - a click on a link marked `data-onboarding-step` completes that step. Several steps of the
 *   creation are plain links drawn by Server Components (the starting point, the choice of
 *   suggestion), so the mark is an attribute and not a handler: nothing about how they look or
 *   navigate changes.
 *
 * Mounted once in the root layout; renders nothing.
 */
export function OnboardingFunnelTracker() {
  const pathname = usePathname();
  const isFunnel = resolvePage(pathname).pageType === "onboarding";

  useEffect(() => {
    if (!isFunnel) return;

    const onPageHide = (event: PageTransitionEvent) => {
      // A page going into the back/forward cache is not gone for good.
      if (!event.persisted) reportAbandonment();
    };
    const onPageShow = () => resetOnboardingNavigation();
    const onClick = (event: MouseEvent) => {
      if (event.button !== 0) return;

      const link = (event.target as Element | null)?.closest?.(`[${ONBOARDING_STEP_ATTRIBUTE}]`);
      const step = link?.getAttribute(ONBOARDING_STEP_ATTRIBUTE);
      if (step) completeOnboardingStep(step as OnboardingStep);
    };

    window.addEventListener("pagehide", onPageHide);
    window.addEventListener("pageshow", onPageShow);
    document.addEventListener("click", onClick);

    return () => {
      window.removeEventListener("pagehide", onPageHide);
      window.removeEventListener("pageshow", onPageShow);
      document.removeEventListener("click", onClick);
    };
  }, [isFunnel]);

  return null;
}
